import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_LOCAL_TRANSPORT_TRUCKING
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

const LIST_LIMIT = 5000;

/** Normalize invoice numbers for duplicate checks (trim + uppercase). */
export const normalizeTruckingInvoiceNo = (value) =>
  String(value || '').trim().toUpperCase();

/** True when invoice has a value that another row already uses. */
export function isDuplicateTruckingInvoice(rows, invoiceNo, excludeDocumentId = '') {
  const key = normalizeTruckingInvoiceNo(invoiceNo);
  if (!key) return false;
  return rows.some(
    (row) =>
      row.documentId !== excludeDocumentId &&
      normalizeTruckingInvoiceNo(row.invoiceNo) === key
  );
}

/** Check Appwrite for an existing invoice number (excluding one document). */
export async function checkTruckingInvoiceExists(invoiceNo, excludeDocumentId = '') {
  const key = normalizeTruckingInvoiceNo(invoiceNo);
  if (!key) return false;
  try {
    const res = await databases.listDocuments(DATABASE_ID, COLLECTION_ID_LOCAL_TRANSPORT_TRUCKING, [
      Query.equal('invoice_no', invoiceNo.trim()),
      Query.limit(10)
    ]);
    return res.documents.some((doc) => {
      if (excludeDocumentId && doc.$id === excludeDocumentId) return false;
      return normalizeTruckingInvoiceNo(doc.invoice_no) === key;
    });
  } catch (e) {
    console.error('checkTruckingInvoiceExists:', e);
    if (e.code === 404) return false;
    throw e;
  }
}

/** Map an Appwrite document to the row shape used by the page. */
export function mapTruckingFromDoc(doc) {
  return {
    documentId: doc.$id,
    lot: doc.lot || '',
    transporter: doc.transporter || '',
    po: doc.po || '',
    materialTransported: doc.material_transported || '',
    invoiceNo: doc.invoice_no || '',
    noOfTrucks: doc.no_of_trucks != null ? Number(doc.no_of_trucks) : 0,
    pricePerTrip: doc.price_per_trip != null ? Number(doc.price_per_trip) : 0,
    vatApplies: doc.vat_applies === true,
    destination: doc.destination || '',
    dnNo: doc.dn_no || '',
    dnDate: doc.dn_date || '',
    exemptionName: doc.exemption_name || '',
    status: doc.status || '',
    datePaid: doc.date_paid || '',
    orderIndex: doc.order_index != null ? Number(doc.order_index) : 0
  };
}

/** Map a page row to the Appwrite attribute payload. */
function mapTruckingToPayload(row) {
  return {
    lot: row.lot || '',
    transporter: row.transporter || '',
    po: row.po || '',
    material_transported: row.materialTransported || '',
    invoice_no: row.invoiceNo || '',
    no_of_trucks: Number(row.noOfTrucks) || 0,
    price_per_trip: Number(row.pricePerTrip) || 0,
    vat_applies: row.vatApplies === true,
    destination: row.destination || '',
    dn_no: row.dnNo || '',
    dn_date: row.dnDate || '',
    exemption_name: row.exemptionName || '',
    status: row.status || '',
    date_paid: row.datePaid || '',
    order_index: Number(row.orderIndex) || 0
  };
}

export async function fetchLocalTransportTrucking() {
  try {
    const res = await databases.listDocuments(DATABASE_ID, COLLECTION_ID_LOCAL_TRANSPORT_TRUCKING, [
      Query.limit(LIST_LIMIT)
    ]);
    return res.documents
      .map(mapTruckingFromDoc)
      .sort((a, b) => a.orderIndex - b.orderIndex);
  } catch (e) {
    console.error('fetchLocalTransportTrucking:', e);
    if (e.code === 404) return [];
    throw e;
  }
}

export async function createLocalTransportTrucking(row) {
  const doc = await databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_LOCAL_TRANSPORT_TRUCKING,
    ID.unique(),
    mapTruckingToPayload(row)
  );
  return mapTruckingFromDoc(doc);
}

export async function updateLocalTransportTrucking(documentId, row) {
  const doc = await databases.updateDocument(
    DATABASE_ID,
    COLLECTION_ID_LOCAL_TRANSPORT_TRUCKING,
    documentId,
    mapTruckingToPayload(row)
  );
  return mapTruckingFromDoc(doc);
}

export async function deleteLocalTransportTrucking(documentId) {
  await databases.deleteDocument(DATABASE_ID, COLLECTION_ID_LOCAL_TRANSPORT_TRUCKING, documentId);
}
